import { useState } from 'react';
import { TaskPage } from '../../../components/workbook/TaskPage';
import { TextInput, TextArea } from '../../../components/workbook/forms';
import { Callout } from '../../../components/workbook/Callout';
import { RankMark } from '@/components/prioritization/RankMark';

interface ShortlistItem {
  id: string;
  rank: string;
  title: string;
  rationale: string;
  dependency: string;
}

interface CutItem {
  id: string;
  item: string;
  reason: string;
}

export default function RankedShortlistPage() {
  const [shortlist, setShortlist] = useState<ShortlistItem[]>([
    { id: '1', rank: '1', title: '', rationale: '', dependency: '' },
    { id: '2', rank: '2', title: '', rationale: '', dependency: '' },
    { id: '3', rank: '3', title: '', rationale: '', dependency: '' },
  ]);
  const [cutList, setCutList] = useState<CutItem[]>([
    { id: '1', item: '', reason: '' },
  ]);

  const updateShortlist = (id: string, field: keyof ShortlistItem, value: string) => {
    setShortlist(shortlist.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addShortlistItem = () => {
    if (shortlist.length < 7) {
      setShortlist([...shortlist, { id: Date.now().toString(), rank: (shortlist.length + 1).toString(), title: '', rationale: '', dependency: '' }]);
    }
  };

  const updateCutItem = (id: string, field: keyof CutItem, value: string) => {
    setCutList(cutList.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const addCutItem = () => {
    if (cutList.length < 5) {
      setCutList([...cutList, { id: Date.now().toString(), item: '', reason: '' }]);
    }
  };

  return (
    <TaskPage
      phase="arrange"
      title="Ranked Shortlist"
      pageNumber={6}
      instruction="Create a ranked shortlist (≤ 7 items). For each: Title — 1-sentence rationale (criteria + DIBB) — key dependency."
      whyItMatters="Visible rationale improves recall and later commitment; keep lines short and scannable (anti-clutter)."
      coachRail={{
        timing: "10-20 minutes",
        tips: "Keep it to 7 items max. Each item needs: title, rationale, dependency.",
        debrief: "Why did #1 beat #2? Is the shortlist clear?"
      }}
    >
      <div className="space-y-6">
        <div className="bg-[var(--surface)] p-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Ranked Shortlist (≤ 7 items)</h3>
          <div className="space-y-4">
            {shortlist.map((item) => (
              <div key={item.id} className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
                <div className="flex items-center gap-3 mb-3">
                  <RankMark rank={Number(item.rank) || null} official={false} />
                  <TextInput
                    value={item.title}
                    onChange={(e) => updateShortlist(item.id, 'title', e.target.value)}
                    placeholder="Item title"
                    className="flex-1"
                  />
                </div>
                <TextArea
                  label="1-sentence rationale (criteria + DIBB)"
                  value={item.rationale}
                  onChange={(e) => updateShortlist(item.id, 'rationale', e.target.value)}
                  placeholder="Why this item? What criteria? What's the evidence (DIBB)?"
                  rows={2}
                />
                <TextInput
                  label="Key dependency"
                  value={item.dependency}
                  onChange={(e) => updateShortlist(item.id, 'dependency', e.target.value)}
                  placeholder="What must happen first?"
                  className="mt-2"
                />
              </div>
            ))}
            {shortlist.length < 7 && (
              <button
                onClick={addShortlistItem}
                className="w-full py-2 px-4 border-2 border-dashed border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--rule)] hover:text-[var(--text-sec)] transition-colors"
              >
                + Add another item (max 7)
              </button>
            )}
          </div>
        </div>

        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-6">
          <h3 className="text-lg font-semibold text-[var(--coral)] mb-4">Cut List (3-5 items)</h3>
          <p className="text-sm text-[var(--coral)] mb-4">What we're not doing now (and why)</p>
          <div className="space-y-3">
            {cutList.map((cut) => (
              <div key={cut.id} className="bg-[var(--bg-raised)] border border-[var(--coral)] rounded p-3">
                <TextInput
                  value={cut.item}
                  onChange={(e) => updateCutItem(cut.id, 'item', e.target.value)}
                  placeholder="Item we're cutting"
                  className="mb-2"
                />
                <TextInput
                  value={cut.reason}
                  onChange={(e) => updateCutItem(cut.id, 'reason', e.target.value)}
                  placeholder="Why we're not doing it now"
                />
              </div>
            ))}
            {cutList.length < 5 && (
              <button
                onClick={addCutItem}
                className="w-full py-2 px-4 border-2 border-dashed border-[var(--coral)] text-[var(--coral)] hover:border-[var(--coral)] hover:text-[var(--coral)] transition-colors"
              >
                + Add to cut list (max 5)
              </button>
            )}
          </div>
        </div>

        <Callout type="tip" phase="arrange">
          <p className="text-sm">Keep rationale short and scannable. Include criteria and DIBB (Data/Insight/Belief/Bet) for each item.</p>
        </Callout>
      </div>
    </TaskPage>
  );
}






